import type {
  NotificationDeliveryReason,
  SendableNotificationChannel,
} from './notificationChannel.ts';

/** Whether a notification left for this person, or why it did not. */
export type NotificationDeliveryStatus = 'sent' | 'failed';

/**
 * Why a notification did not reach one person. Named after the situation, like
 * the reasons the console already shows before a send: staff read these.
 */
export type NotificationFailureReason =
  | NotificationDeliveryReason
  | 'settled'
  | 'cannot_see_roleplay'
  | 'already_played'
  | 'roleplay_unavailable'
  | 'nothing_to_say'
  | 'magic_link_disabled'
  | 'expired'
  | 'send_failed';

/** What became of a sent mail, as far as the mail provider has told us. */
export type NotificationOutcome = 'delivered' | 'bounced' | 'complained';

/** How many deliveries of one send ended each way. */
export interface NotificationSendCounts {
  delivered: number;
  bounced: number;
  complained: number;
  failed: number;
  /** Left, with nothing heard about it since: every chat card, and a mail not yet confirmed. */
  sent: number;
}

/** Who a press in the console was aimed at: one person, an organisation or a cohort. */
export const notificationAudienceKinds = ['user', 'organisation', 'cohort'] as const;

export type NotificationAudienceKind = (typeof notificationAudienceKinds)[number];

export interface NotificationAudience {
  kind: NotificationAudienceKind;
  id: string;
}

/** The families the history sorts its notifications into, in the order it lists them. */
export const notificationHistoryGroups = [
  'meeting',
  'moment',
  'journey',
  'email',
] as const;

export type NotificationHistoryGroup = (typeof notificationHistoryGroups)[number];

/** One notification the history can hold, as its type filter offers it. */
export interface NotificationHistoryKind {
  kind: string;
  label: string;
  group: NotificationHistoryGroup;
}

/** Whether a send was a press in the console or went out on its own. */
export const notificationSendOrigins = ['automatic', 'console'] as const;

export type NotificationSendOrigin = (typeof notificationSendOrigins)[number];

/** One send in the history: a press in the console, or a day of automatic sends. */
export interface NotificationSendSummary {
  id: string;
  kind: string;
  label: string;
  channels: SendableNotificationChannel[];
  /** Null for a press that reached people of more than one organisation. */
  organisation_id: string | null;
  organisation_name: string | null;
  /** Null when nobody pressed anything: Wundo sent it on its own. */
  sent_by: { user_id: string; name: string | null } | null;
  sent_at: string;
  /**
   * Who the press was aimed at, as staff read it: "Cohort Acme — Q1",
   * "Organisation Acme" or a person's name. Null for an automatic send, and
   * for an audience that no longer exists.
   */
  audience: string | null;
  recipient_count: number;
  counts: NotificationSendCounts;
  /** False for sends from before the content of each message was kept. */
  content_kept: boolean;
}

/** One person a send was meant for. */
export interface NotificationDeliveryItem {
  id: string;
  user_id: string;
  name: string | null;
  email: string | null;
  channel: SendableNotificationChannel;
  status: NotificationDeliveryStatus;
  failure_reason: NotificationFailureReason | null;
  /** The worst news about a sent mail; null for a chat card or a mail nothing was heard about. */
  outcome: NotificationOutcome | null;
  language: string | null;
  sent_at: string;
  delivered_at: string | null;
  bounced_at: string | null;
  complained_at: string | null;
  has_content: boolean;
}

/**
 * What one person received, as they saw it. Every link that would sign them in
 * or unsubscribe them reads as a placeholder.
 */
export interface NotificationDeliveryContentResponse {
  id: string;
  channel: SendableNotificationChannel;
  subject: string | null;
  html: string | null;
  /** Every Adaptive Card Teams was handed, a carousel's pages in order. */
  cards: Record<string, unknown>[] | null;
  blocks: Record<string, unknown>[] | null;
  /** When the nightly retention wiped it; null while it is still kept. */
  content_removed_at: string | null;
  content_kept: boolean;
}
